import { useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { MoreHorizontal, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { PageHeader } from "@/components/shared/page-header";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useClients } from "@/features/clients/api";
import { useDeleteDispatchNote, useDispatchNotes } from "@/features/dispatch/api";
import { DISPATCH_STATUSES, labelFor, statusTone } from "@/features/dispatch/constants";
import type { DispatchNoteListRow, DispatchStatus } from "@/features/dispatch/types";
import { fmtQty } from "@/features/inventory/constants";
import { isSupabaseConfigured } from "@/lib/supabase";

export function DispatchPage() {
  return (
    <ErrorBoundary title="Dispatch crashed">
      <DispatchInner />
    </ErrorBoundary>
  );
}

function DispatchInner() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { so?: string; client?: string };
  const { data = [], isLoading, error, refetch, isFetching } = useDispatchNotes(
    search.so ? { soId: search.so } : search.client ? { clientId: search.client } : undefined,
  );
  const { data: clients = [] } = useClients();
  const remove = useDeleteDispatchNote();

  const [statusFilter, setStatusFilter] = useState<"all" | DispatchStatus>("all");
  const [clientFilter, setClientFilter] = useState(search.client ?? "all");
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState<DispatchNoteListRow | null>(null);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return data.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (clientFilter !== "all" && row.client_id !== clientFilter) return false;
      if (!query) return true;
      return [row.note_number, row.so_number, row.client_name, row.client_code, row.vehicle_number]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [data, statusFilter, clientFilter, q]);

  const kpis = useMemo(
    () => ({
      draft: data.filter((r) => r.status === "draft").length,
      loading: data.filter((r) => r.status === "loading").length,
      dispatched: data.filter((r) => r.status === "dispatched").length,
    }),
    [data],
  );

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to use dispatch."
      />
    );
  }

  return (
    <div className="space-y-3">
      <PageHeader
        title="Dispatch"
        description="Loading slips · SO fulfillments · optional invoice"
        actions={
          <>
            <Button type="button" variant="outline" size="sm" disabled={isFetching} onClick={() => refetch()}>
              <RefreshCw className={isFetching ? "animate-spin" : ""} />
              Refresh
            </Button>
            <Button type="button" size="sm" onClick={() => navigate({ to: "/dispatch/new" })}>
              <Plus />
              New dispatch
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-end gap-2 text-xs">
        <div className="rounded-md border border-border bg-card px-2.5 py-1.5">
          <div className="text-2xs text-muted-foreground">Draft</div>
          <div className="font-semibold tabular-nums">{kpis.draft}</div>
        </div>
        <div className="rounded-md border border-border bg-card px-2.5 py-1.5">
          <div className="text-2xs text-muted-foreground">Loading</div>
          <div className="font-semibold tabular-nums">{kpis.loading}</div>
        </div>
        <div className="rounded-md border border-border bg-card px-2.5 py-1.5">
          <div className="text-2xs text-muted-foreground">Dispatched</div>
          <div className="font-semibold tabular-nums">{kpis.dispatched}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search note, SO, vehicle…"
            className="pl-7"
          />
        </div>
        <NativeSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          className="w-32"
        >
          <option value="all">All status</option>
          {DISPATCH_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          className="w-40"
        >
          <option value="all">All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} — {c.name}
            </option>
          ))}
        </NativeSelect>
      </div>

      {error ? <p className="text-sm text-destructive">{(error as Error).message}</p> : null}

      {!isLoading && filtered.length === 0 ? (
        <EmptyState
          title="No dispatch notes"
          description="Create a note from fulfilled sales order stock to load and dispatch."
          action={
            <Button size="sm" onClick={() => navigate({ to: "/dispatch/new" })}>
              <Plus />
              New dispatch
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Note</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Client / SO</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead className="text-right">Weight</TableHead>
              <TableHead>Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Link
                      to="/dispatch/$dnId"
                      params={{ dnId: row.id }}
                      className="font-mono text-xs font-medium text-primary hover:underline"
                    >
                      {row.note_number}
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {format(parseISO(row.dispatch_date), "dd MMM yyyy")}
                  </TableCell>
                  <TableCell>
                    <div className="text-xs font-medium">{row.client_name}</div>
                    <div className="text-2xs text-muted-foreground">{row.so_number}</div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {row.vehicle_number || row.transporter_name || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmtQty(row.total_weight)}</TableCell>
                  <TableCell>
                    <StatusDot tone={statusTone(row.status)}>
                      {labelFor(DISPATCH_STATUSES, row.status)}
                    </StatusDot>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" variant="ghost" size="icon-sm">
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to="/dispatch/$dnId" params={{ dnId: row.id }}>
                            Open
                          </Link>
                        </DropdownMenuItem>
                        {row.status === "draft" || row.status === "loading" ? (
                          <DropdownMenuItem asChild>
                            <Link to="/dispatch/$dnId/edit" params={{ dnId: row.id }}>
                              Edit
                            </Link>
                          </DropdownMenuItem>
                        ) : null}
                        {row.status === "draft" ? (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleting(row)}
                            >
                              <Trash2 />
                              Delete
                            </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => {
          if (!o) setDeleting(null);
        }}
        title="Delete draft dispatch?"
        description={deleting ? `${deleting.note_number} will be removed.` : undefined}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}
